import {
  ATTENTION_TIMESHEET_STATUSES,
  EDITABLE_TIMESHEET_STATUSES,
  highestPriorityStatus,
  isAttentionStatus,
  isTimesheetEditable,
  TIMESHEET_STATUS_PRIORITY,
  TIMESHEET_STATUSES,
  type TimesheetStatus,
} from '@clockwise/shared';
import { z } from 'zod';

// Re-export from `@clockwise/shared` so callers of this package (the web
// app, the typed API client) don't need to know there's a separate
// shared package for status constants. One import path, one source.
export {
  ATTENTION_TIMESHEET_STATUSES,
  EDITABLE_TIMESHEET_STATUSES,
  highestPriorityStatus,
  isAttentionStatus,
  isTimesheetEditable,
  TIMESHEET_STATUS_PRIORITY,
  TIMESHEET_STATUSES,
  type TimesheetStatus,
};

export const TimesheetSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  // Nullable on editable rows; the API rejects null at submit time.
  customerId: z.string().uuid().nullable(),
  userId: z.string().uuid(),
  // The member's assigned customer-team role (team-inclusion + approval
  // routing) and the activity's own role (reporting + billing rate). Both
  // nullable on editable rows; non-null once submitted/approved.
  customerTeamServiceRoleId: z.string().uuid().nullable(),
  timesheetEntryServiceRoleId: z.string().uuid().nullable(),
  date: z.string(),
  // numeric(6,2) returns as string from drizzle-pg to preserve precision.
  // Nullable on editable rows; locked rows always have hours set.
  hours: z.string().nullable(),
  productId: z.string().uuid().nullable(),
  // The engagement (SOW) this entry's time is attributed to. Null when nothing
  // covers the entry, or while an ambiguous row is still waiting for a pick —
  // both are legitimate draft states; only the ambiguous one blocks submit.
  engagementId: z.string().uuid().nullable(),
  // Legacy fields — see apps/api/sql/timesheets.sql header. New writes leave them null.
  serviceId: z.string().uuid().nullable(),
  serviceVersion: z.number().int().nullable(),
  description: z.string().nullable(),
  currency: z.string().nullable(),
  billable: z.boolean(),
  status: z.enum(TIMESHEET_STATUSES),
  // Populated on rejected / failed; cleared on resubmit. Lets the grid
  // render the badge tooltip without joining to approval_instances.
  resolutionReason: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Timesheet = z.infer<typeof TimesheetSchema>;

/** Lazily-fetched resolution details for a rejected/failed entry. `resolvedAt`
 *  is the ISO timestamp of the terminal decision; `rejectedByName` is the
 *  rejecting approver's display name, null for system-driven outcomes. */
export const TimesheetRejectionSchema = z.object({
  resolvedAt: z.string().nullable(),
  rejectedByName: z.string().nullable(),
});
export type TimesheetRejection = z.infer<typeof TimesheetRejectionSchema>;

// customerId + hours are optional/nullable: the day view saves the moment
// the user types any single field. The server returns `VALIDATION_ERROR`
// at submit time if a row is still incomplete.
export const CreateTimesheetInput = z.object({
  customerId: z.string().uuid().nullable().optional(),
  userId: z.string().uuid().optional(),
  // Sent only to answer a ROLE_REQUIRED prompt (activity loggable under >1 of
  // the user's roles). Omitted otherwise; the server auto-picks. The activity
  // role is always derived server-side from the product, never sent.
  customerTeamServiceRoleId: z.string().uuid().optional(),
  date: z.string(),
  hours: z.number().positive().nullable().optional(),
  productId: z.string().uuid().nullable().optional(),
  // Sent when the client has resolved one — either the single engagement
  // covering the entry, or the one the user picked from an ambiguous set.
  engagementId: z.string().uuid().nullable().optional(),
  description: z.string().nullable().optional(),
  billable: z.boolean().optional(),
});
export type CreateTimesheetInput = z.infer<typeof CreateTimesheetInput>;

export const PatchTimesheetInput = z.object({
  customerId: z.string().uuid().nullable().optional(),
  productId: z.string().uuid().nullable().optional(),
  // Explicit null clears the attribution. Omitted leaves it alone — except on
  // a customer move, which clears it server-side.
  engagementId: z.string().uuid().nullable().optional(),
  // See CreateTimesheetInput — only sent to answer a ROLE_REQUIRED prompt.
  customerTeamServiceRoleId: z.string().uuid().optional(),
  hours: z.number().positive().nullable().optional(),
  description: z.string().nullable().optional(),
  billable: z.boolean().optional(),
});
export type PatchTimesheetInput = z.infer<typeof PatchTimesheetInput>;

export interface SubmitRangeInput {
  fromDate: string;
  toDate: string;
}

/** Copy last week. Only the TARGET week's first day travels; the source is
 *  the preceding 7 days, derived server-side. */
export interface CopyWeekInput {
  weekStart: string;
}

/**
 * Outcome of a copy. `copied + failed` is what the server attempted; the four
 * skip counts explain what it declined to attempt and why — enough for the
 * caller to say "3 copied · 2 already filled" without recomputing anything.
 */
export interface CopyWeekResult {
  copied: number;
  failed: number;
  /** Target cells already filled — what makes a second click idempotent. */
  duplicate: number;
  /** Target days the user is no longer on that customer's team. */
  offTeam: number;
  /** Prior rows carrying no hours (null / 0) — nothing worth carrying. */
  zeroHours: number;
  /** Prior rows with no customer. */
  incomplete: number;
  /** Messages from the rows in `failed`. */
  errors: string[];
  entries: Timesheet[];
}

export interface RecallRangeInput {
  fromDate: string;
  toDate: string;
  reason?: string;
}

export interface ListTimesheetsQuery {
  userId?: string;
  customerId?: string;
  fromDate?: string;
  toDate?: string;
  status?: TimesheetStatus;
  billable?: boolean;
  sortField?: 'date' | 'createdAt' | 'updatedAt' | 'hours';
  sortDirection?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface RangeQuery {
  userId: string;
  fromDate: string;
  toDate: string;
}
